import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAdminResetPasswordMutation } from "@/features/api";
import { showApiError } from "@/lib/api-client";
import { AuthCard } from "@/pages/login";

const MIN_LENGTH = 6;

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";
  const reset = useAdminResetPasswordMutation();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");

  const mismatch = confirm.length > 0 && password !== confirm;
  const tooShort = password.length > 0 && password.length < MIN_LENGTH;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (password !== confirm || password.length < MIN_LENGTH) return;
    try {
      await reset.mutateAsync({ token, password });
      toast.success("Şifren belirlendi. Şimdi giriş yapabilirsin.");
      navigate("/login", { replace: true });
    } catch (err) {
      showApiError(err);
    }
  }

  if (!token) {
    return (
      <AuthCard title="Link geçersiz" description="Şifre linki eksik veya bozuk.">
        <p className="text-center text-sm">
          <Link to="/forgot-password" className="font-medium hover:underline">
            Yeni link iste
          </Link>
        </p>
      </AuthCard>
    );
  }

  return (
    <AuthCard title="Yeni şifre" description={`Admin şifresi en az ${MIN_LENGTH} karakter olmalı.`}>
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="password">Şifre</Label>
          <Input
            id="password"
            type="password"
            autoComplete="new-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {tooShort && (
            <p className="text-xs text-destructive">En az {MIN_LENGTH} karakter.</p>
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="confirm">Şifre (tekrar)</Label>
          <Input
            id="confirm"
            type="password"
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
          {mismatch && <p className="text-xs text-destructive">Şifreler eşleşmiyor.</p>}
        </div>
        <Button
          type="submit"
          className="w-full"
          disabled={reset.isPending || mismatch || password.length < MIN_LENGTH}
        >
          {reset.isPending ? "Kaydediliyor..." : "Şifreyi kaydet"}
        </Button>
      </form>
    </AuthCard>
  );
}
